export const CHART_AXIS_GROUPINGS = {
  LOCATION: 'location',
  MATERIAL: 'material',
  BUILDING_BLOCK: 'building_block',
  LABEL: 'label'
} as const

export const CHART_Y_MEASURES = {
  TOTAL: 'total',
  PRODUCTION: 'production',
  BUILDING: 'building',
  USE_MAINTENANCE: 'use_maintenance',
  DEMOLITION_PROCESSING: 'demolition_processing'
} as const

export const DEFAULT_CHART_AXIS_GROUPING = CHART_AXIS_GROUPINGS.LOCATION
export const DEFAULT_CHART_Y_MEASURE = CHART_Y_MEASURES.TOTAL

export const CHART_DISPLAY_MODES = {
  MATERIAL_STACK: 'material_stack',
  BUILDING_BLOCK_STACK: 'building_block_stack',
  LABEL_STACK: 'label_stack',
  PHASE_STACK: 'phase_stack',
  PHASE_GROUPED: 'phase_grouped'
} as const

export const DEFAULT_DISPLAY_MODE = CHART_DISPLAY_MODES.MATERIAL_STACK

// Which dimension each stacking display mode groups the stacked segments by.
export const STACK_DIMENSION_BY_DISPLAY_MODE: Record<string, string> = {
  [CHART_DISPLAY_MODES.MATERIAL_STACK]: CHART_AXIS_GROUPINGS.MATERIAL,
  [CHART_DISPLAY_MODES.BUILDING_BLOCK_STACK]: CHART_AXIS_GROUPINGS.BUILDING_BLOCK,
  [CHART_DISPLAY_MODES.LABEL_STACK]: CHART_AXIS_GROUPINGS.LABEL
}

export const LIFECYCLE_PHASE_GROUPS = {
  PRODUCTION: 'production',
  BUILDING_A4: 'a4',
  BUILDING_A5: 'a5',
  USE_MAINTENANCE: 'use_maintenance',
  DEMOLITION_C1: 'c1',
  DEMOLITION_C2: 'c2',
  DEMOLITION_C3_C4: 'c3_c4',
  END_OF_LIFE: 'end_of_life'
} as const

export const CHART_AXIS_GROUPING_OPTIONS = [
  { value: CHART_AXIS_GROUPINGS.LOCATION, title: 'Location' },
  { value: CHART_AXIS_GROUPINGS.MATERIAL, title: 'Material' },
  { value: CHART_AXIS_GROUPINGS.BUILDING_BLOCK, title: 'Building Block' },
  { value: CHART_AXIS_GROUPINGS.LABEL, title: 'Label' }
]

export const CHART_Y_MEASURE_OPTIONS = [
  { value: CHART_Y_MEASURES.TOTAL, title: 'Total MKI' },
  { value: CHART_Y_MEASURES.PRODUCTION, title: 'Production (A1-A3)' },
  { value: CHART_Y_MEASURES.BUILDING, title: 'Building (A4-A5)' },
  { value: CHART_Y_MEASURES.USE_MAINTENANCE, title: 'Use & Maintenance (B)' },
  { value: CHART_Y_MEASURES.DEMOLITION_PROCESSING, title: 'Demolition & Processing (C)' }
]

export const CHART_DISPLAY_MODE_OPTIONS = [
  { value: CHART_DISPLAY_MODES.MATERIAL_STACK, title: 'Material (stacked)' },
  { value: CHART_DISPLAY_MODES.BUILDING_BLOCK_STACK, title: 'Building Block (stacked)' },
  { value: CHART_DISPLAY_MODES.LABEL_STACK, title: 'Label (stacked)' },
  { value: CHART_DISPLAY_MODES.PHASE_STACK, title: 'Lifecycle (stacked)' },
  {
    value: CHART_DISPLAY_MODES.PHASE_GROUPED,
    title: 'Lifecycle (separated bars)'
  }
]

export const CHART_SORT_ORDERS = {
  NONE: 'none',
  ASC: 'asc',
  DESC: 'desc'
} as const

export const DEFAULT_SORT_ORDER = CHART_SORT_ORDERS.NONE

export const CHART_SORT_ORDER_OPTIONS = [
  { value: CHART_SORT_ORDERS.NONE, title: 'Default (A-Z)' },
  { value: CHART_SORT_ORDERS.ASC, title: 'Impact: low to high' },
  { value: CHART_SORT_ORDERS.DESC, title: 'Impact: high to low' }
]

export const LIFECYCLE_PHASE_GROUP_OPTIONS = [
  { value: LIFECYCLE_PHASE_GROUPS.PRODUCTION, title: 'Production (A1-A3)' },
  { value: LIFECYCLE_PHASE_GROUPS.BUILDING_A4, title: 'A4' },
  { value: LIFECYCLE_PHASE_GROUPS.BUILDING_A5, title: 'A5' },
  { value: LIFECYCLE_PHASE_GROUPS.USE_MAINTENANCE, title: 'Use & Maintenance (B)' },
  { value: LIFECYCLE_PHASE_GROUPS.DEMOLITION_C1, title: 'C1' },
  { value: LIFECYCLE_PHASE_GROUPS.DEMOLITION_C2, title: 'C2' },
  { value: LIFECYCLE_PHASE_GROUPS.DEMOLITION_C3_C4, title: 'C3-C4' },
  { value: LIFECYCLE_PHASE_GROUPS.END_OF_LIFE, title: 'End-of-Life (D)' }
]

export const DEFAULT_SELECTED_PHASES: string[] = LIFECYCLE_PHASE_GROUP_OPTIONS.map(
  (option) => option.value
)

export type LifecyclePhaseGroup =
  (typeof LIFECYCLE_PHASE_GROUPS)[keyof typeof LIFECYCLE_PHASE_GROUPS]

export const LIFECYCLE_PHASE_GROUP_COLORS: Record<LifecyclePhaseGroup, string> = {
  [LIFECYCLE_PHASE_GROUPS.PRODUCTION]: '#1b7837',
  [LIFECYCLE_PHASE_GROUPS.BUILDING_A4]: '#4393c3',
  [LIFECYCLE_PHASE_GROUPS.BUILDING_A5]: '#2166ac',
  [LIFECYCLE_PHASE_GROUPS.USE_MAINTENANCE]: '#b36b00',
  [LIFECYCLE_PHASE_GROUPS.DEMOLITION_C1]: '#f4a582',
  [LIFECYCLE_PHASE_GROUPS.DEMOLITION_C2]: '#d6604d',
  [LIFECYCLE_PHASE_GROUPS.DEMOLITION_C3_C4]: '#b2182b',
  [LIFECYCLE_PHASE_GROUPS.END_OF_LIFE]: '#762a83'
}
